import React, { useState, useEffect } from 'react';
import { runFullQASuite, QASummary, QATestResult } from '../lib/qaRunner';

interface SystemQADiagnosticsModalProps {
  onClose: () => void;
}

export const SystemQADiagnosticsModal: React.FC<SystemQADiagnosticsModalProps> = ({ onClose }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string>('all');
  const [qaSummary, setQaSummary] = useState<QASummary | null>(null);
  const [expandedTestIds, setExpandedTestIds] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<{ current: number; total: number }>({ current: 0, total: 0 });
  const [copySuccess, setCopySuccess] = useState(false);

  const startQARun = async () => {
    setIsRunning(true);
    setProgress({ current: 0, total: 12 });
    
    try {
      const summary = await runFullQASuite((curr, total) => {
        setProgress({ current: curr, total });
      });
      setQaSummary(summary);
    } catch (e) {
      console.error("QA Run error", e);
    } finally {
      setIsRunning(false);
    }
  };

  // Run automatically on first open
  useEffect(() => {
    startQARun();
  }, []);

  const toggleExpand = (id: string) => {
    setExpandedTestIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    if (qaSummary) {
      setExpandedTestIds(new Set(qaSummary.results.map(r => r.id)));
    }
  };

  const collapseAll = () => {
    setExpandedTestIds(new Set());
  };

  const filteredResults = qaSummary?.results.filter(r => {
    if (activeCategoryFilter === 'all') return true;
    return r.category === activeCategoryFilter;
  }) || [];

  const handleExportJSON = () => {
    if (!qaSummary) return;
    const blob = new Blob([JSON.stringify(qaSummary, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `edugravity-qa-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyMarkdown = () => {
    if (!qaSummary) return;
    let md = `# 🛡️ EduGravity Automated QA & Diagnostic Report\n\n`;
    md += `**Date:** ${new Date(qaSummary.timestamp).toLocaleString()}\n`;
    md += `**Overall Result:** ${qaSummary.failed === 0 ? '✅ 100% PASSED' : `⚠️ ${qaSummary.failed} FAILING`}\n`;
    md += `**Tests:** ${qaSummary.passed}/${qaSummary.total} Passed in ${qaSummary.totalDurationMs}ms\n\n`;
    md += `| Category | Test Name | Status | Duration |\n`;
    md += `| :--- | :--- | :---: | :---: |\n`;
    
    qaSummary.results.forEach(r => {
      md += `| ${r.categoryLabel} | ${r.name} | ${r.status === 'passed' ? '✅ PASS' : '❌ FAIL'} | ${r.durationMs}ms |\n`;
    });

    navigator.clipboard.writeText(md);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);
  };

  const categoryCounts = React.useMemo(() => {
    if (!qaSummary) return {};
    const counts: Record<string, { total: number; passed: number; failed: number }> = {};
    qaSummary.results.forEach(r => {
      if (!counts[r.category]) counts[r.category] = { total: 0, passed: 0, failed: 0 };
      counts[r.category].total++;
      if (r.status === 'passed') counts[r.category].passed++;
      else counts[r.category].failed++;
    });
    return counts;
  }, [qaSummary]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-indigo-600 text-white flex items-center justify-center shadow-md">
              <span className="material-icons-round text-2xl">health_and_safety</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-extrabold text-lg text-slate-900 dark:text-white">
                  Automated QA &amp; Health Diagnostics
                </h2>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                  Isolated Sandbox
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Synthetic verification across Super Admin, Principal, Teacher, Parent &amp; BYOK pipelines.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={startQARun}
              disabled={isRunning}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-md flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <span className={`material-icons-round text-base ${isRunning ? 'animate-spin' : ''}`}>
                {isRunning ? 'sync' : 'play_arrow'}
              </span>
              {isRunning ? `Testing (${progress.current}/${progress.total})...` : 'Re-run QA Suite'}
            </button>

            <button
              onClick={handleCopyMarkdown}
              disabled={!qaSummary}
              className="px-3 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold rounded-xl border border-slate-300 dark:border-slate-700 flex items-center gap-1 transition-colors cursor-pointer"
              title="Copy Summary Markdown"
            >
              <span className="material-icons-round text-sm">
                {copySuccess ? 'check' : 'content_copy'}
              </span>
              {copySuccess ? 'Copied!' : 'Copy'}
            </button>

            <button
              onClick={handleExportJSON}
              disabled={!qaSummary}
              className="px-3 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold rounded-xl border border-slate-300 dark:border-slate-700 flex items-center gap-1 transition-colors cursor-pointer"
              title="Download JSON Report"
            >
              <span className="material-icons-round text-sm">file_download</span>
              Export JSON
            </button>

            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
            >
              <span className="material-icons-round">close</span>
            </button>
          </div>
        </div>

        {/* Real-time Summary Banner */}
        {qaSummary && (
          <div className="px-6 py-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${qaSummary.failed === 0 ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                <span className="font-bold text-slate-800 dark:text-slate-200">
                  {qaSummary.failed === 0 ? 'System Status: All Tests Operational' : `System Status: ${qaSummary.failed} Test(s) Failing`}
                </span>
              </div>
              <span className="text-slate-400">|</span>
              <span className="text-slate-600 dark:text-slate-400">
                Passed: <strong className="text-emerald-600 dark:text-emerald-400">{qaSummary.passed}</strong> / {qaSummary.total}
              </span>
              <span className="text-slate-400">|</span>
              <span className="text-slate-600 dark:text-slate-400">
                Duration: <strong>{qaSummary.totalDurationMs}ms</strong>
              </span>
            </div>

            <div className="flex items-center gap-2 text-[11px]">
              <button 
                onClick={expandAll}
                className="text-indigo-600 dark:text-indigo-400 hover:underline font-semibold"
              >
                Expand All Assertions
              </button>
              <span className="text-slate-300 dark:text-slate-700">•</span>
              <button 
                onClick={collapseAll}
                className="text-slate-500 hover:underline font-semibold"
              >
                Collapse All
              </button>
            </div>
          </div>
        )}

        {/* Category Navigation Pills */}
        <div className="px-6 py-2.5 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 flex flex-wrap items-center gap-2 overflow-x-auto">
          {[
            { key: 'all', label: 'All Tests', icon: 'view_list' },
            { key: 'super_admin', label: '👑 Super Admin', icon: 'admin_panel_settings' },
            { key: 'principal', label: '🏫 Principal', icon: 'school' },
            { key: 'teacher', label: '👩‍🏫 Teacher', icon: 'edit_note' },
            { key: 'parent', label: '👨‍👩‍👧 Parent', icon: 'family_restroom' },
            { key: 'policies_gating', label: '🔒 Policies & Gating', icon: 'tune' },
            { key: 'byok_ai', label: '🔑 BYOK & AI', icon: 'psychology' },
          ].map(tab => {
            const count = tab.key === 'all' 
              ? qaSummary?.total 
              : categoryCounts[tab.key]?.total;
            const hasFail = tab.key === 'all' 
              ? (qaSummary?.failed || 0) > 0 
              : (categoryCounts[tab.key]?.failed || 0) > 0;

            return (
              <button
                key={tab.key}
                onClick={() => setActiveCategoryFilter(tab.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer ${
                  activeCategoryFilter === tab.key
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
              >
                <span>{tab.label}</span>
                {count !== undefined && (
                  <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-extrabold ${
                    hasFail 
                      ? 'bg-red-500 text-white' 
                      : (activeCategoryFilter === tab.key ? 'bg-indigo-700 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300')
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Test List Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-100/50 dark:bg-slate-950/30">
          {isRunning && !qaSummary && (
            <div className="h-64 flex flex-col items-center justify-center text-center">
              <span className="material-icons-round text-5xl text-indigo-500 animate-spin mb-3">sync</span>
              <h3 className="font-bold text-slate-700 dark:text-slate-200">Executing Synthetic QA Test Matrix...</h3>
              <p className="text-xs text-slate-500 mt-1">Verifying role journeys, policy waterfalls, and BYOK routing.</p>
            </div>
          )}

          {filteredResults.map((test) => {
            const isExpanded = expandedTestIds.has(test.id);
            const isPass = test.status === 'passed';

            return (
              <div
                key={test.id}
                className={`bg-white dark:bg-slate-900 rounded-xl border transition-all ${
                  isPass 
                    ? 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 shadow-xs' 
                    : 'border-red-300 dark:border-red-800 shadow-md'
                }`}
              >
                {/* Test Item Header */}
                <div 
                  className="p-4 flex items-start justify-between gap-3 cursor-pointer select-none"
                  onClick={() => toggleExpand(test.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                      isPass 
                        ? 'bg-emerald-100 dark:bg-emerald-950/70 text-emerald-600 dark:text-emerald-400' 
                        : 'bg-red-100 dark:bg-red-950/70 text-red-600 dark:text-red-400'
                    }`}>
                      <span className="material-icons-round text-lg">
                        {isPass ? 'check_circle' : 'cancel'}
                      </span>
                    </div>

                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                          {test.categoryLabel}
                        </span>
                        <h4 className="font-bold text-sm text-slate-900 dark:text-slate-100">
                          {test.name}
                        </h4>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {test.description}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-[11px] font-mono text-slate-400">
                      {test.durationMs}ms
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wider ${
                      isPass 
                        ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800' 
                        : 'bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
                    }`}>
                      {isPass ? 'PASS' : 'FAIL'}
                    </span>
                    <span className={`material-icons-round text-slate-400 text-base transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      expand_more
                    </span>
                  </div>
                </div>

                {/* Expandable Assertions Table */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-950/40 rounded-b-xl">
                    <h5 className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 mt-2">
                      Verified Assertions ({test.assertions.filter(a => a.passed).length}/{test.assertions.length})
                    </h5>

                    <div className="space-y-1.5">
                      {test.assertions.map((assertion, idx) => (
                        <div 
                          key={idx}
                          className="flex items-start justify-between gap-3 text-xs p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800"
                        >
                          <div className="flex items-start gap-2">
                            <span className={`material-icons-round text-sm mt-0.5 ${
                              assertion.passed ? 'text-emerald-500' : 'text-red-500'
                            }`}>
                              {assertion.passed ? 'check' : 'close'}
                            </span>
                            <div>
                              <p className="font-semibold text-slate-800 dark:text-slate-200">
                                {assertion.name}
                              </p>
                              {assertion.error && (
                                <p className="text-[11px] text-red-500 font-mono mt-0.5">
                                  {assertion.error}
                                </p>
                              )}
                            </div>
                          </div>

                          {(assertion.expected !== undefined || assertion.actual !== undefined) && (
                            <div className="text-[10px] font-mono text-right shrink-0">
                              <span className="text-slate-400">Exp: </span>
                              <span className="text-slate-600 dark:text-slate-300">{String(assertion.expected)}</span>
                              <br />
                              <span className="text-slate-400">Act: </span>
                              <span className={assertion.passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                                {String(assertion.actual)}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <span className="material-icons-round text-sm text-indigo-500">info</span>
            <span>All tests execute synthetically in memory without altering cloud or database records.</span>
          </div>
          <button 
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-bold rounded-lg transition-colors cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
